import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useUserData } from '../hooks/useUserData';
import { useContracts } from '../hooks/useContract';
import { PageLoader, Spinner } from '../components/common/Loader';
import NetworkGuard from '../components/common/NetworkGuard';
import BinaryTree from '../components/team/BinaryTree';
import LevelExplorer from '../components/team/LevelExplorer';
import { formatUSDT } from '../utils/format';
import '../components/team/Team.css';

function TeamStat({ label, value, unit, sub }) {
  return (
    <div className="team-stat">
      <div className="team-stat-label">{label}</div>
      <div className="team-stat-value">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
      {sub && <div className="team-stat-sub">{sub}</div>}
    </div>
  );
}

function LevelsPanel({ userId }) {
  const { read } = useContracts();
  const [config, setConfig] = useState(null);
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      read.storage.getAllLevelConfig(),
      userId ? read.storage.getEligibleLevels(userId) : Promise.resolve(null),
    ]).then(([cfg, elig]) => {
      if (cancelled) return;
      setConfig(cfg);
      setEligibility(elig);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (!config) {
    return (
      <div className="tree-panel">
        <div style={{ textAlign: 'center', padding: 40 }}><Spinner size="lg" /></div>
      </div>
    );
  }

  return (
    <div className="tree-panel">
      <div className="tree-header">
        <div>
          <h3>Level Rewards Structure</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            You earn a % of every purchase made by users in your downline, up to 12 levels deep.
            Each level requires a minimum number of qualifying directs.
          </div>
        </div>
      </div>

      <div className="levels-grid">
        {config.bps.map((bps, i) => (
          <LevelExplorer
            key={i}
            rootId={userId}
            levelIndex={i}
            bps={bps}
            directsRequired={config.directsReq[i]}
            eligible={eligibility ? eligibility[i] : false}
          />
        ))}
      </div>
    </div>
  );
}

export default function TeamPage() {
  const { isConnected, connect, connecting } = useWallet();
  const { user, userId, isRegistered, loading } = useUserData();
  const navigate = useNavigate();

  if (!isConnected) {
    return (
      <div className="container team-page">
        <div className="empty-state">
          <div className="icon">🔒</div>
          <h3 style={{ marginBottom: 8 }}>Connect Your Wallet</h3>
          <button className="btn btn-primary btn-lg mt-4" onClick={connect} disabled={connecting}>
            {connecting ? <><Spinner /> Connecting</> : 'Connect Wallet'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <NetworkGuard>
      <div className="container team-page">
        {loading ? (
          <PageLoader label="LOADING TEAM" sub="Fetching your network..." />
        ) : !isRegistered ? (
          <div className="empty-state">
            <div className="icon">📝</div>
            <h3>Please Register First</h3>
            <button className="btn btn-primary btn-lg mt-4" onClick={() => navigate('/register')}>
              Register Now →
            </button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8 }}>
              <div className="section-eyebrow">MY TEAM</div>
              <h1>Team & <span className="text-gradient">Downline</span></h1>
              <p style={{ color: 'var(--text-muted)', marginTop: 6 }}>
                ID <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>{userId}</span> ·
                {' '}Overview of your directs and multi-level rewards.
              </p>
            </div>

            <div className="team-stats-grid">
              <TeamStat
                label="👥 Direct Referrals"
                value={Number(user.directReferrals)}
                sub="People you sponsored"
              />
              <TeamStat
                label="✓ Qualifying Directs"
                value={Number(user.qualifyingDirects)}
                sub="Directs who have purchased a machine"
              />
              <TeamStat
                label="Status"
                value={user.isActive ? '● Active' : '○ Inactive'}
                sub={user.isActive ? 'Earning rewards' : 'Need an active machine'}
              />
            </div>

            <LevelsPanel userId={userId} />

            <BinaryTree myUserId={userId} />
          </>
        )}
      </div>
    </NetworkGuard>
  );
}
